import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { TrashIcon, CheckCircleIcon, NoSymbolIcon, ShieldExclamationIcon, EyeIcon, ShoppingCartIcon, ArrowUturnLeftIcon, DevicePhoneMobileIcon, EnvelopeIcon, KeyIcon, CakeIcon, ChevronDownIcon, MagnifyingGlassIcon, UserCircleIcon, CalendarDaysIcon, AtSymbolIcon, PhoneIcon } from '@heroicons/react/24/outline';
import { FaGoogle } from 'react-icons/fa';
import { Disclosure, Transition, Tab } from '@headlessui/react';

// statusTranslations and statusColors are not directly used in the main panel, but are part of the modal which is in the same file. I'll keep them.
const statusTranslations = {
    pending: 'Hazırlanıyor',
    shipped: 'Kargolandı',
    delivered: 'Teslim Edildi',
    cancelled: 'İptal Edildi',
    refunded: 'İade Edildi',
    return_requested: 'İade Talebi',
    return_approved: 'İade Onaylandı',
    return_rejected: 'İade Reddedildi',
    return_shipped: 'İade Kargoya Verildi',
    return_received: 'İade Kargo Alındı',
    return_completed: 'İade Tamamlandı',
    pending_approval: 'Onay Bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi',
    completed: 'Tamamlandı',
    shipped: 'Kargoya Verildi',
    received: 'Kargo Alındı'
};
const statusColors = {
    pending: 'bg-yellow-100 text-yellow-800',
    shipped: 'bg-blue-100 text-blue-800',
    delivered: 'bg-green-100 text-green-800',
    cancelled: 'bg-red-100 text-red-800',
    refunded: 'bg-purple-100 text-purple-800',
    return_requested: 'bg-orange-100 text-orange-800',
    return_approved: 'bg-teal-100 text-teal-800',
    return_rejected: 'bg-red-100 text-red-800',
    return_shipped: 'bg-purple-100 text-purple-800',
    return_received: 'bg-indigo-100 text-indigo-800',
    return_completed: 'bg-green-100 text-green-800',
    pending_approval: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-blue-100 text-blue-800',
    rejected: 'bg-red-100 text-red-800',
    completed: 'bg-green-100 text-green-800',
    shipped: 'bg-purple-100 text-purple-800',
    received: 'bg-indigo-100 text-indigo-800'
};

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

// UserDetailsModal remains the same.
const UserDetailsModal = ({ user, closeModal, token, onBanToggle, onDelete }) => {
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const userId = user.id;

    useEffect(() => {
        const fetchDetails = async () => {
            console.log(`[UserDetailsModal] Detaylar getiriliyor. Kullanıcı ID: ${userId}`);
            if (!userId || !token) {
                console.warn('[UserDetailsModal] fetchDetails çağrıldı ancak userId veya token eksik.');
                setLoading(false); // Eksik bilgi varsa yüklemeyi durdur.
                return;
            }
            setLoading(true);
            try {
                console.log(`[UserDetailsModal] API isteği gönderiliyor: /api/users/${userId}/details`);
                const data = await api.get(`/api/users/${userId}/details`, { headers: { 'Authorization': `Bearer ${token}` } });
                console.log('[UserDetailsModal] API\'den başarılı detay yanıtı alındı:', data);
                setDetails(data);
            } catch (err) {
                console.error('[UserDetailsModal] Kullanıcı detayları getirilirken hata yakalandı:', err);
                if (err.response) {
                    console.error('[UserDetailsModal] Hata Detayları (Response):', {
                        data: err.response.data,
                        status: err.response.status,
                    });
                } else if (err.request) {
                    console.error('[UserDetailsModal] Hata Detayları (Request): Sunucudan yanıt alınamadı.');
                } else {
                    console.error('[UserDetailsModal] Hata Detayları (Genel): İstek hazırlığında hata.', err.message);
                }
                toast.error(err.response?.data?.error || "Kullanıcı detayları getirilemedi. Detaylar için konsolu kontrol edin.");
                closeModal();
            } finally {
                setLoading(false);
            }
        };
        fetchDetails();
    }, [userId, token, closeModal]);

    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric'}) : '-';

    if (loading) return <div className="p-10 text-center">Detaylar yükleniyor...</div>;
    if (!details) return <div className="p-10 text-center">Kullanıcı bilgileri bulunamadı.</div>;

    const { profile, addresses, orders, returns, stats, provider } = details;
    const email = profile?.email || 'E-posta Yok';
    const fullName = profile?.full_name || 'İsimsiz Kullanıcı';

    const ProviderIcon = ({ provider }) => provider === 'google' ? <FaGoogle className="h-5 w-5 text-red-500" /> : <KeyIcon className="h-5 w-5 text-gray-500" />;
    const providerText = provider === 'google' ? 'Google ile Kayıt' : 'E-posta/Şifre';

    const StatCard = ({ icon, label, value, colorClass }) => (
        <div className="flex items-center p-3 bg-gray-50 rounded-lg shadow-sm border">
            <div className={`p-3 rounded-full ${colorClass}`}>{icon}</div>
            <div className="ml-4">
                <p className="text-xl font-bold text-gray-800">{value}</p>
                <p className="text-xs text-gray-600 font-medium">{label}</p>
            </div>
        </div>
    );

    return (
        <div className="p-2 sm:p-4 max-w-6xl w-full bg-gray-50 rounded-lg">
             <div className="flex justify-between items-start mb-6 bg-white p-4 rounded-lg shadow-sm border">
                <div>
                    <h3 className="text-2xl font-bold text-gray-800">{fullName}</h3>
                    <p className="text-sm text-gray-500 flex items-center"><EnvelopeIcon className="h-4 w-4 mr-1.5"/> {email}</p>
                    <p className="text-sm text-gray-500 flex items-center mt-1"><ProviderIcon provider={provider} /> <span className="ml-1.5">{providerText}</span></p>
                </div>
                <div className="text-right">
                    <p className="text-xs text-gray-400">Kullanıcı ID</p>
                    <p className="text-xs font-mono text-gray-600">{userId}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <StatCard icon={<ShoppingCartIcon className="h-6 w-6 text-blue-600"/>} label="Toplam Sipariş" value={stats?.totalOrders || 0} colorClass="bg-blue-100" />
                <StatCard icon={<NoSymbolIcon className="h-6 w-6 text-red-600"/>} label="İptal Edilen" value={stats?.totalCancelled || 0} colorClass="bg-red-100" />
                <StatCard icon={<ArrowUturnLeftIcon className="h-6 w-6 text-purple-600"/>} label="Tamamlanan İade" value={stats?.totalRefunded || 0} colorClass="bg-purple-100" />
                <StatCard icon={<CalendarDaysIcon className="h-6 w-6 text-green-600"/>} label="Kayıt Tarihi" value={formatDate(profile?.created_at)} colorClass="bg-green-100" />
            </div>

            <Tab.Group>
                <Tab.List className="flex space-x-1 rounded-xl bg-brand-green p-1">
                    {['Siparişler', 'İadeler', 'Adresler', 'Profil Detayları'].map((category) => (
                        <Tab key={category} className={({ selected }) => classNames(
                            'w-full rounded-lg py-2.5 text-sm font-medium leading-5',
                            'ring-white/60 ring-offset-2 ring-offset-brand-green-light focus:outline-none focus:ring-2',
                            selected ? 'bg-white text-brand-green shadow' : 'text-white hover:bg-white/[0.15]'
                        )}>
                            {category}
                        </Tab>
                    ))}
                </Tab.List>
                <Tab.Panels className="mt-2">
                    <Tab.Panel className="rounded-xl bg-white p-3 min-h-[300px]">
                        <div className="border rounded-lg max-h-96 overflow-y-auto">
                            {orders && orders.length > 0 ? (
                                orders.map(order => (
                                    <Disclosure key={order.id} as="div" className="border-b last:border-b-0">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="w-full p-3 text-sm flex justify-between items-center hover:bg-gray-100">
                                                    <div className="truncate flex items-center gap-3">
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[order.status]}`}>{statusTranslations[order.status]}</span>
                                                        <span className="font-medium">MINELA-{order.order_number}</span>
                                                        <span className="text-gray-500">{formatDate(order.created_at)}</span>
                                                    </div>
                                                    <ChevronDownIcon className={`ml-2 h-5 w-5 text-gray-500 transform transition-transform ${open ? 'rotate-180' : ''}`} />
                                                </Disclosure.Button>
                                                <Transition enter="transition duration-100 ease-out" enterFrom="transform scale-95 opacity-0" enterTo="transform scale-100 opacity-100" leave="transition duration-75 ease-out" leaveFrom="transform scale-100 opacity-100" leaveTo="transform scale-95 opacity-0">
                                                    <Disclosure.Panel className="p-3 bg-gray-50 border-t">
                                                        {order.order_items?.map(item => (
                                                            <div key={item.id} className="flex items-center justify-between py-1">
                                                                <div className="flex items-center">
                                                                    <img src={item.products?.main_image_url || 'https://placehold.co/64x64'} alt={item.products?.name} className="w-8 h-8 rounded object-cover mr-2" />
                                                                    <p className="text-xs text-gray-700">{item.products?.name || 'Silinmiş Ürün'} ({item.quantity} adet)</p>
                                                                </div>
                                                                <p className="text-xs font-semibold">₺{item.price_at_purchase}</p>
                                                            </div>
                                                        ))}
                                                        <p className="text-right font-bold mt-2">Toplam: ₺{order.total_price}</p>
                                                    </Disclosure.Panel>
                                                </Transition>
                                            </>
                                        )}
                                    </Disclosure>
                                ))
                            ) : <p className="p-10 text-sm text-gray-500 text-center">Kullanıcının hiç siparişi yok.</p>}
                        </div>
                    </Tab.Panel>
                    <Tab.Panel className="rounded-xl bg-white p-3 min-h-[300px]">
                        <div className="border rounded-lg max-h-96 overflow-y-auto">
                            {returns && returns.length > 0 ? (
                                returns.map(ret => (
                                    <Disclosure key={ret.id} as="div" className="border-b last:border-b-0">
                                        {({ open }) => (
                                            <>
                                                <Disclosure.Button className="w-full p-3 text-sm flex justify-between items-center hover:bg-gray-100">
                                                    <div className="truncate flex items-center gap-3">
                                                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${statusColors[ret.status]}`}>{statusTranslations[ret.status]}</span>
                                                        <span className="font-medium">Sipariş MINELA-{ret.orders?.order_number || 'Bilinmeyen'}</span>
                                                        <span className="text-gray-500">{formatDate(ret.created_at)}</span>
                                                    </div>
                                                    <ChevronDownIcon className={`ml-2 h-5 w-5 text-gray-500 transform transition-transform ${open ? 'rotate-180' : ''}`} />
                                                </Disclosure.Button>
                                                <Transition enter="transition duration-100 ease-out" enterFrom="transform scale-95 opacity-0" enterTo="transform scale-100 opacity-100" leave="transition duration-75 ease-out" leaveFrom="transform scale-100 opacity-100" leaveTo="transform scale-95 opacity-0">
                                                    <Disclosure.Panel className="p-3 bg-gray-50 border-t">
                                                        <p className="text-xs font-semibold mb-1">İade Edilen Ürünler:</p>
                                                        {ret.return_items?.map(item => (
                                                            <div key={item.id} className="flex items-center py-1">
                                                                <img src={item.products?.main_image_url || 'https://placehold.co/64x64'} alt={item.products?.name} className="w-8 h-8 rounded object-cover mr-2" />
                                                                <p className="text-xs text-gray-700">{item.products?.name || 'Silinmiş Ürün'} ({item.quantity} adet)</p>
                                                            </div>
                                                        ))}
                                                        <p className="text-xs font-semibold mt-2 mb-1">İade Nedeni:</p>
                                                        <p className="text-xs text-gray-600 p-2 bg-gray-100 rounded">{ret.reason}</p>
                                                    </Disclosure.Panel>
                                                </Transition>
                                            </>
                                        )}
                                    </Disclosure>
                                ))
                            ) : <p className="p-10 text-sm text-gray-500 text-center">Kullanıcının hiç iade talebi yok.</p>}
                        </div>
                    </Tab.Panel>
                    <Tab.Panel className="rounded-xl bg-white p-3 min-h-[300px]">
                        <div className="max-h-96 overflow-y-auto p-1 space-y-3">
                            {addresses && addresses.length > 0 ? (
                                addresses.map((addr) => (
                                    <div key={addr.id} className="p-4 bg-white rounded-lg shadow-sm border">
                                        <p className="font-bold text-gray-800 mb-2">{addr.address_title}</p>
                                        <dl className="text-sm text-gray-600 space-y-1">
                                            <div className="flex"><dt className="w-28 font-semibold text-gray-500">Alıcı:</dt><dd>{addr.full_name}</dd></div>
                                            <div className="flex"><dt className="w-28 font-semibold text-gray-500">Adres:</dt><dd>{addr.address_line}</dd></div>
                                            <div className="flex"><dt className="w-28 font-semibold text-gray-500">Şehir/İlçe:</dt><dd>{addr.city}, {addr.district}</dd></div>
                                            <div className="flex"><dt className="w-28 font-semibold text-gray-500">Ülke:</dt><dd>{addr.country}</dd></div>
                                            <div className="flex items-center"><dt className="w-28 font-semibold text-gray-500 flex items-center"><PhoneIcon className="h-4 w-4 mr-1.5"/>Telefon:</dt><dd>{addr.phone}</dd></div>
                                        </dl>
                                    </div>
                                ))
                            ) : <p className="text-sm text-gray-500 p-10 text-center">Kayıtlı adresi yok.</p>}
                        </div>
                    </Tab.Panel>
                    <Tab.Panel className="rounded-xl bg-white p-3 min-h-[300px]">
                        <div className="border rounded-lg p-4 bg-white">
                            <dl className="text-sm text-gray-700 space-y-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><UserCircleIcon className="h-4 w-4 mr-2"/>Tam Adı:</dt><dd className="md:col-span-2">{profile?.full_name || 'Belirtilmemiş'}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><AtSymbolIcon className="h-4 w-4 mr-2"/>E-posta:</dt><dd className="md:col-span-2">{profile?.email}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><PhoneIcon className="h-4 w-4 mr-2"/>Telefon:</dt><dd className="md:col-span-2">{profile?.phone || 'Belirtilmemiş'}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><CalendarDaysIcon className="h-4 w-4 mr-2"/>Kayıt Tarihi:</dt><dd className="md:col-span-2">{formatDate(profile?.created_at)}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><CalendarDaysIcon className="h-4 w-4 mr-2"/>Son Giriş:</dt><dd className="md:col-span-2">{profile?.last_sign_in_at ? formatDate(profile.last_sign_in_at) : 'Bilinmiyor'}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2 border-b"><dt className="font-semibold text-gray-500 flex items-center"><CheckCircleIcon className="h-4 w-4 mr-2"/>E-posta Onayı:</dt><dd className="md:col-span-2">{profile?.email_confirmed_at ? `Evet (${formatDate(profile.email_confirmed_at)})` : 'Hayır'}</dd></div>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 py-2"><dt className="font-semibold text-gray-500 flex items-center"><ShieldExclamationIcon className="h-4 w-4 mr-2"/>Yasaklı mı?:</dt><dd className="md:col-span-2">{profile?.is_banned ? 'Evet' : 'Hayır'}</dd></div>
                            </dl>
                        </div>
                    </Tab.Panel>
                </Tab.Panels>
            </Tab.Group>

            <div className="mt-6 pt-6 border-t flex flex-col sm:flex-row justify-between items-center gap-4">
                <div className="flex items-center gap-3">
                    {user.is_banned ? (
                        <button onClick={onBanToggle} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 shadow-sm disabled:opacity-50" disabled={loading}>
                            <CheckCircleIcon className="h-5 w-5 mr-2" /> Yasağı Kaldır
                        </button>
                    ) : (
                        <button onClick={onBanToggle} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700 shadow-sm disabled:opacity-50" disabled={loading}>
                            <ShieldExclamationIcon className="h-5 w-5 mr-2" /> Kullanıcıyı Yasakla
                        </button>
                    )}
                    <button onClick={onDelete} className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 shadow-sm disabled:opacity-50" disabled={loading}>
                        <TrashIcon className="h-5 w-5 mr-2" /> Kullanıcıyı Sil
                    </button>
                </div>
                <button onClick={closeModal} className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 py-2 px-4 rounded-md text-sm font-semibold">Kapat</button>
            </div>
        </div>
    );
};

// Kullanıcı Engelleme Modalı
const BanUserModal = ({ user, onConfirm, closeModal }) => {
    const banDurations = [
        { label: '1 Gün Engelle', value: '1d', className: 'bg-yellow-500 hover:bg-yellow-600' },
        { label: '7 Gün Engelle', value: '7d', className: 'bg-yellow-600 hover:bg-yellow-700' },
        { label: 'Kalıcı Olarak Engelle', value: '100 years', className: 'bg-red-700 hover:bg-red-800' },
    ];

    return (
        <div className="p-4 max-w-md mx-auto text-center">
            <ShieldExclamationIcon className="mx-auto h-12 w-12 text-yellow-500" />
            <h3 className="mt-2 text-lg font-medium">Kullanıcıyı Engelle</h3>
            <p className="mt-2 text-sm text-gray-500">
                <strong>{user.full_name || user.email}</strong> için bir engelleme süresi seçin.
            </p>
            <div className="mt-6 grid grid-cols-1 gap-3">
                {banDurations.map(duration => (
                    <button
                        key={duration.value}
                        onClick={() => onConfirm(duration.value)}
                        className={`w-full px-4 py-2 text-sm text-white rounded-md shadow-sm ${duration.className}`}
                    >
                        {duration.label}
                    </button>
                ))}
            </div>
            <button onClick={closeModal} className="mt-4 w-full bg-gray-200 hover:bg-gray-300 py-2 rounded-md text-sm">
                İptal
            </button>
        </div>
    );
};

function UserManagementPanel({ openModal, closeModal }) {
    const { token } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUserId, setSelectedUserId] = useState(null);

    const fetchUsers = useCallback(async () => {
        // Eğer token henüz AuthContext'ten gelmediyse, işlemi atla.
        // `loading` durumunu `false` yapmıyoruz ki "yükleniyor" yazısı kaybolmasın.
        // Token geldiğinde useEffect bunu tekrar tetikleyecek.
        if (!token) {
            console.log('[UserManagementPanel] Token bekleniyor, API isteği şimdilik atlanıyor.');
            return;
        }

        setLoading(true); // API isteği yapacağımız için yükleme durumunu başlatıyoruz.
        console.log('[UserManagementPanel] Kullanıcıları getirme işlemi başlatıldı (Token mevcut).');
        
        try {
            const requestUrl = '/api/users';
            const requestHeaders = { 'Authorization': `Bearer ${token}` };
            
            console.log(`[UserManagementPanel] API isteği gönderiliyor. URL: ${requestUrl}`);

            const data = await api.get(requestUrl, { headers: requestHeaders });
            
            console.log('[UserManagementPanel] API\'den başarılı yanıt alındı. Gelen veri:', data);
            
            if (Array.isArray(data)) {
                console.log(`[UserManagementPanel] ${data.length} adet kullanıcı verisi state'e set ediliyor.`);
                setUsers(data);
            } else {
                console.warn('[UserManagementPanel] UYARI: API\'den gelen veri bir dizi (array) değil. Arayüzde sorun yaşanabilir.', data);
                setUsers([]); // Hata almamak için boş dizi ata
            }

        } catch (error) {
            console.error('[UserManagementPanel] Kullanıcıları getirirken bir hata yakalandı. Tüm hata objesi:', error);
            if (error.response) {
                console.error('[UserManagementPanel] Hata Detayları (Response):', { data: error.response.data, status: error.response.status });
                toast.error(`Sunucu Hatası (${error.response.status}): ${error.response.data?.error || 'Bilinmeyen hata'}`);
            } else if (error.request) {
                console.error('[UserManagementPanel] Hata Detayları (Request): Sunucudan yanıt alınamadı.');
                toast.error('Sunucuya ulaşılamadı. Lütfen internet bağlantınızı veya sunucu durumunu kontrol edin.');
            } else {
                console.error('[UserManagementPanel] Hata Detayları (Genel): İstek hazırlığında hata.', error.message);
                toast.error(`Bir hata oluştu: ${error.message}`);
            }
        } finally {
            setLoading(false);
            console.log('[UserManagementPanel] İşlem tamamlandı, yükleme durumu false olarak ayarlandı.');
        }
    }, [token]);

    useEffect(() => {
        fetchUsers();
    }, [fetchUsers]);

    const handleBan = async (userId, duration) => {
        const isUnbanning = duration === 'none';
        
        // Sadece yasak kaldırma işleminde direkt onayla. Yasaklama modal üzerinden yapıldığı için onaya gerek yok.
        if (isUnbanning && !window.confirm('Bu kullanıcının yasağını gerçekten kaldırmak istiyor musunuz?')) {
            return;
        }

        console.log(`[UserManagementPanel] Kullanıcı ban/unban işlemi başlatıldı. ID: ${userId}, Süre: ${duration}`);
        try {
            await api.put(`/api/users/${userId}/ban`, 
                { ban_duration: duration }, 
                {
                    headers: { 
                        'Content-Type': 'application/json', // Sunucuya JSON gönderdiğimizi belirtiyoruz.
                        'Authorization': `Bearer ${token}` 
                    }
                }
            );
            toast.success(isUnbanning ? 'Kullanıcı yasağı başarıyla kaldırıldı.' : 'Kullanıcı başarıyla yasaklandı.');
            fetchUsers(); // Listeyi yenile
        } catch (error) {
            console.error(`[UserManagementPanel] Kullanıcı ban/unban işlemi sırasında hata:`, error);
            toast.error(error.response?.data?.error || 'İşlem sırasında bir hata oluştu.');
        }
    };

    const handleDelete = async (userId) => {
        if (window.confirm('Bu kullanıcıyı kalıcı olarak silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
            console.log(`[UserManagementPanel] Kullanıcı silme işlemi başlatıldı. ID: ${userId}`);
            try {
                await api.delete(`/api/users/${userId}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                toast.success('Kullanıcı başarıyla silindi.');
                fetchUsers(); // Listeyi yenile
            } catch (error) {
                console.error('[UserManagementPanel] Kullanıcı silinirken hata:', error);
                toast.error(error.response?.data?.error || 'Kullanıcı silinirken bir hata oluştu. Detaylar için konsolu kontrol edin.');
            }
        }
    };

    const openBanModal = (user) => {
        openModal(
            <BanUserModal
                user={user}
                closeModal={closeModal}
                onConfirm={(duration) => {
                    closeModal();
                    handleBan(user.id, duration);
                }}
            />
        );
    };


    const handleViewDetails = (user) => {
        setSelectedUserId(user.id);
        openModal(
            <UserDetailsModal 
                user={user} 
                closeModal={() => {
                    closeModal();
                    setSelectedUserId(null);
                }} 
                token={token} 
                onBanToggle={() => {
                    closeModal();
                    if (user.is_banned) {
                        handleBan(user.id, 'none'); // Yasağı kaldır
                    } else {
                        openBanModal(user); // Yasaklama modalını aç
                    }
                }}
                onDelete={() => {
                    closeModal();
                    handleDelete(user.id);
                }}
            />
        );
    };

    const filteredUsers = users.filter(user =>
        (user.full_name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
        (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
    
    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('tr-TR') : '-';

    return (
        <div className="container mx-auto p-2 sm:p-4">
            <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
                <div className="bg-gradient-to-r from-brand-green to-brand-green-light p-4 sm:p-6 text-white">
                    <h2 className="text-xl sm:text-2xl font-bold mb-1 sm:mb-2">Kullanıcı Yönetimi</h2>
                    <p className="opacity-90 text-sm sm:text-base">Kullanıcıları görüntüleyin ve yönetin</p>
                </div>
                
                <div className="p-4 sm:p-6">
                    <div className="mb-4 relative">
                        <input
                    type="text"
                    placeholder="Kullanıcı adı veya e-posta ile ara..."
                    className="w-full p-2 pl-10 border rounded-lg focus:ring-brand-green focus:border-brand-green"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                />
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            </div>

            {loading ? (
                <p>Kullanıcılar yükleniyor...</p>
            ) : (
                <>
                {/* Desktop Tablo - Sadece büyük ekranlarda görünür */}
                <div className="hidden lg:block overflow-x-auto bg-white rounded-lg shadow">
                    <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kullanıcı</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Kayıt Tarihi</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Durum</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">İşlemler</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {filteredUsers.map((user) => (
                                <tr key={user.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                                            <div className="text-sm text-gray-500 ml-2">({user.email})</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(user.created_at)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {user.is_banned ? (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">Yasaklı</span>
                                        ) : (
                                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Aktif</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <button onClick={() => handleViewDetails(user)} className="text-indigo-600 hover:text-indigo-900 p-1" title="Detayları Görüntüle"><EyeIcon className="h-5 w-5"/></button>
                                        {user.is_banned ? (
                                            <button onClick={() => handleBan(user.id, 'none')} className="text-green-600 hover:text-green-900 ml-2 p-1" title="Yasağı Kaldır"><CheckCircleIcon className="h-5 w-5"/></button>
                                        ) : (
                                            <button onClick={() => openBanModal(user)} className="text-yellow-600 hover:text-yellow-900 ml-2 p-1" title="Kullanıcıyı Yasakla"><ShieldExclamationIcon className="h-5 w-5"/></button>
                                        )}
                                        <button onClick={() => handleDelete(user.id)} className="text-red-600 hover:text-red-900 ml-2 p-1" title="Kullanıcıyı Sil"><TrashIcon className="h-5 w-5"/></button>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                </div>

                {/* Mobil Kart Görünümü - Sadece küçük ve orta ekranlarda görünür */}
                <div className="lg:hidden space-y-4">
                    {filteredUsers.map((user) => (
                        <div key={user.id} className="bg-white rounded-lg shadow border border-gray-200 p-4">
                            <div className="flex items-start justify-between mb-3">
                                <div className="flex-1">
                                    <h3 className="text-sm font-semibold text-gray-900">{user.full_name}</h3>
                                    <p className="text-xs text-gray-500 mt-1">{user.email}</p>
                                    <p className="text-xs text-gray-500 mt-1">Kayıt: {formatDate(user.created_at)}</p>
                                </div>
                                <div className="ml-3">
                                    {user.is_banned ? (
                                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">Yasaklı</span>
                                    ) : (
                                        <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Aktif</span>
                                    )}
                                </div>
                            </div>
                            <div className="flex justify-end space-x-2">
                                <button 
                                    onClick={() => handleViewDetails(user)} 
                                    className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors" 
                                    title="Detayları Görüntüle"
                                >
                                    <EyeIcon className="h-4 w-4"/>
                                </button>
                                {user.is_banned ? (
                                    <button 
                                        onClick={() => handleBan(user.id, 'none')} 
                                        className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors" 
                                        title="Yasağı Kaldır"
                                    >
                                        <CheckCircleIcon className="h-4 w-4"/>
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => openBanModal(user)} 
                                        className="p-2 text-yellow-600 hover:text-yellow-900 hover:bg-yellow-50 rounded-lg transition-colors" 
                                        title="Kullanıcıyı Yasakla"
                                    >
                                        <ShieldExclamationIcon className="h-4 w-4"/>
                                    </button>
                                )}
                                <button 
                                    onClick={() => handleDelete(user.id)} 
                                    className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors" 
                                    title="Kullanıcıyı Sil"
                                >
                                    <TrashIcon className="h-4 w-4"/>
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                </>
            )}
            {filteredUsers.length === 0 && !loading && <p className="text-center text-gray-500 mt-4">Aramanızla eşleşen kullanıcı bulunamadı.</p>}
                </div>
            </div>
        </div>
    );
}

export default UserManagementPanel;